import { useEffect, useRef, useState } from "react";
import { useMemberStore } from "../../stores/memberStore";
import { useServerStore } from "../../stores/serverStore";
import { useModalStore } from "../../stores/modalStore";
import { useChannelStore } from "../../stores/channelStore";
import { useVoiceStore } from "../../stores/voiceStore";
import { Settings } from "lucide-react";
import { MemberSkeleton } from "./Skeletons.jsx";

const ROLE_ORDER = ['owner', 'admin', 'moderator', 'member'];

const ROLE_LABELS = {
  owner: '👑 Owner',
  admin: '⚡ Admins',
  moderator: '🛡️ Moderators',
  member: 'Members',
};

const ROLE_COLORS = {
  owner: 'text-amber-400',
  admin: 'text-rose-400',
  moderator: 'text-emerald-400',
  member: 'text-gray-400',
};

const ROLE_AVATAR_GRADIENT = {
  owner: 'from-amber-500 to-orange-500',
  admin: 'from-rose-500 to-pink-500',
  moderator: 'from-emerald-500 to-cyan-500',
  member: 'from-indigo-500 to-purple-500',
};

const BADGE_META = {
  'Founder':     { label: 'FOUND', bg: 'linear-gradient(90deg,#cc0000,#0044cc,#cc7700)', color: '#fff' },
  'Foundation':  { label: 'FND',   bg: 'rgba(255,0,0,0.15)',   color: '#ff4444', border: '1px solid rgba(255,0,0,0.35)' },
  'Labs':        { label: 'LABS',  bg: 'rgba(255,165,0,0.15)', color: '#ffa500', border: '1px solid rgba(255,165,0,0.35)' },
  'Corporation': { label: 'CORP',  bg: 'rgba(0,102,255,0.15)', color: '#4488ff', border: '1px solid rgba(0,102,255,0.35)' },
};

function MemberBadge({ badge }) {
  if (!badge) return null;
  const meta = BADGE_META[badge] || { label: badge.slice(0,4).toUpperCase(), bg: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' };
  return (
    <span style={{
      fontSize: 8, fontWeight: 800, padding: '1px 4px', borderRadius: 2,
      background: meta.bg, color: meta.color, border: meta.border,
      letterSpacing: '0.3px', flexShrink: 0, whiteSpace: 'nowrap', lineHeight: 1.4,
    }}>{meta.label}</span>
  );
}

function MemberAvatar({ member }) {
  const role = (member.role || 'member').toLowerCase();
  const gradient = ROLE_AVATAR_GRADIENT[role] || ROLE_AVATAR_GRADIENT.member;

  if (member.avatar_url) {
    return (
      <img
        src={member.avatar_url}
        alt={member.name}
        className="w-8 h-8 rounded-full object-cover"
        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
      />
    );
  }

  const initial = (member.name || member.username || '?')[0].toUpperCase();
  return (
    <div className={`rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white font-bold`} style={{ width: 'var(--sz-avatar)', height: 'var(--sz-avatar)', fontSize: 9 }}>
      {member.avatar || initial}
    </div>
  );
}

function RemoteAudio({ stream }) {
  const audioRef = useRef();
  useEffect(() => {
    if (audioRef.current && stream) {
      audioRef.current.srcObject = stream;
    }
  }, [stream]);
  return <audio ref={audioRef} autoPlay playsInline />;
}

function LocalAudio({ localStream }) {
  const audioRef = useRef();
  useEffect(() => {
    if (audioRef.current && localStream) {
      audioRef.current.srcObject = localStream;
    }
  }, [localStream]);
  return <audio ref={audioRef} autoPlay playsInline muted />;
}

export default function MemberSidebar({ onToggleActivity, mobileOpen, onMobileClose }) {
  const members = useMemberStore((state) => state.members);
  const getCurrentUser = useMemberStore((state) => state.getCurrentUser);
  const onOpen = useModalStore((state) => state.onOpen);
  const currentServerId = useServerStore((state) => state.currentServerId);
  const channels = useChannelStore((state) => state.channels);
  const voiceChannels = (channels || []).filter(c => c.type === 'voice');

  const vcChannelId    = useVoiceStore(s => s.channelId);
  const vcChannelName  = useVoiceStore(s => s.channelName);
  const vcParticipants = useVoiceStore(s => s.participants);
  const vcIsMuted      = useVoiceStore(s => s.isMuted);

  const [localMembers, setLocalMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  let currentUser = null;
  try {
    currentUser = getCurrentUser();
  } catch (_) {
    // ignore
  }

  const isAdmin =
    currentUser?.role?.toUpperCase() === 'ADMIN' ||
    currentUser?.role?.toUpperCase() === 'OWNER';

  // Fetch members from API when currentServerId changes
  useEffect(() => {
    if (!currentServerId) return;
    if (currentServerId.startsWith('demo-')) return; // demo servers use local store data
    setMembersLoading(true);
    fetch(`/api/servers/${currentServerId}/members`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
    })
      .then((r) => r.json())
      .then((data) => {
        const list = data.members || data || [];
        if (Array.isArray(list) && list.length > 0) {
          if (useMemberStore.getState().setMembers) {
            useMemberStore.getState().setMembers(list);
          } else {
            setLocalMembers(list);
          }
        }
      })
      .catch(console.error)
      .finally(() => setMembersLoading(false));
  }, [currentServerId]);

  const displayMembers = localMembers.length > 0 ? localMembers : members;

  const joined = false;
  const peers = [];
  const localStream = null;

  // Role-based grouping (primary) with division fallback
  const roleGrouped = {};
  const divisionGrouped = {};
  let hasRoles = false;

  displayMembers.forEach((member) => {
    const roleLower = (member.role || '').toLowerCase();
    if (ROLE_ORDER.includes(roleLower)) {
      hasRoles = true;
      if (!roleGrouped[roleLower]) roleGrouped[roleLower] = [];
      roleGrouped[roleLower].push(member);
    } else {
      const section = member.division || 'Others';
      if (!divisionGrouped[section]) divisionGrouped[section] = [];
      divisionGrouped[section].push(member);
    }
  });

  return (
    <div className={`member-sidebar${mobileOpen ? ' mobile-open' : ''}`}
      role="complementary" aria-label="Members"
    >
      {mobileOpen && (
        <button className="mobile-sidebar-close" onClick={onMobileClose} aria-label="Close members">✕</button>
      )}
      <div className="member-header">
        <span>Members — {displayMembers.length}</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {onToggleActivity && (
            <button
              onClick={onToggleActivity}
              style={{ padding: '4px 6px', borderRadius: 4, background: 'none', border: 'none', color: 'rgb(var(--aethex-dim))', cursor: 'pointer', fontSize: 13 }}
              title="Toggle activity panel"
            >⚡</button>
          )}
          {isAdmin && (
            <button
              onClick={() => onOpen("manageMembers")}
              className="manage-members-button"
              title="Manage members"
            >
              <Settings className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="member-list">
        {membersLoading ? <MemberSkeleton count={8} /> : (<>
        {/* Active voice channel — live participants */}
        {vcChannelId && (
          <div className="member-section">
            <div className="member-section-title" style={{ color: '#22c55e', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ animation: 'pulse 2s infinite', display: 'inline-block' }}>●</span>
              🔊 {vcChannelName || 'Voice'} — {vcParticipants.length}
            </div>
            {vcParticipants.map(p => (
              <div key={p.id} className="member-item" style={{ opacity: 1 }}>
                <div className="relative member-avatar-small flex-shrink-0" style={{ position: 'relative' }}>
                  <div style={{ width: 'var(--sz-avatar)', height: 'var(--sz-avatar)', borderRadius: '50%', background: 'linear-gradient(135deg,#22c55e,#16a34a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#fff' }}>
                    {p.avatar || (p.name || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ position: 'absolute', bottom: -1, right: -1, width: 8, height: 8, borderRadius: '50%', background: '#22c55e', border: '1.5px solid rgb(var(--aethex-panel))' }} />
                </div>
                <div className="member-info flex-1 min-w-0">
                  <span className="member-name">{p.name}{p.isLocal ? ' (You)' : ''}</span>
                </div>
                <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                  {(p.isLocal ? vcIsMuted : p.isMuted) && <span style={{ fontSize: 10 }}>🔇</span>}
                  {p.isCameraOn  && <span style={{ fontSize: 10 }}>📷</span>}
                  {p.isSpeaking  && <span style={{ fontSize: 10, color: '#22c55e' }}>🎤</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Idle voice channels (no one connected) */}
        {voiceChannels.filter(vc => vc.id !== vcChannelId).length > 0 && (
          <div className="member-section">
            <div className="member-section-title" style={{ color: 'rgb(var(--aethex-dim))' }}>
              Voice Channels
            </div>
            {voiceChannels.filter(vc => vc.id !== vcChannelId).map(vc => (
              <div key={vc.id} style={{ padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 6, color: 'rgb(var(--aethex-dim))', fontSize: 11 }}>
                <span>🔊</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vc.name}</span>
              </div>
            ))}
          </div>
        )}

        {/* Show all connected voice users */}
        {(joined || (peers && peers.length > 0)) && (
          <div className="member-section">
            <div className="member-section-title" style={{ color: '#0066ff' }}>
              Voice Channel — Nexus Lounge
            </div>
            {joined && (
              <div className="member-item voice-active">
                <div className="member-avatar-small">
                  <span role="img" aria-label="mic">🎤</span>
                  <div className="online-indicator"></div>
                </div>
                <div className="member-name">You (Voice Connected)</div>
                <div className="member-activity">Live</div>
                <LocalAudio localStream={localStream} />
              </div>
            )}
            {peers && peers.map(({ peerId, stream }) => (
              <div key={peerId} className="member-item voice-peer">
                <div className="member-avatar-small">
                  <span role="img" aria-label="mic">🎤</span>
                  <div className="online-indicator"></div>
                </div>
                <div className="member-name">{peerId}</div>
                <div className="member-activity">Live</div>
                <RemoteAudio stream={stream} />
              </div>
            ))}
          </div>
        )}

        {/* Role-based groups (if roles are present) */}
        {hasRoles && ROLE_ORDER.filter(role => roleGrouped[role]?.length > 0).map((role) => (
          <div key={role} className="member-section">
            <div className={`member-section-title ${ROLE_COLORS[role]}`}>
              {ROLE_LABELS[role]} — {roleGrouped[role].length}
            </div>
            {roleGrouped[role].map((member) => (
              <div
                key={member.id}
                className="member-item group relative"
                onClick={() => onOpen("userProfile", { member })}
              >
                <div className="relative member-avatar-small flex-shrink-0">
                  <MemberAvatar member={member} />
                  {member.status !== "offline" && (
                    <div
                      className={`online-indicator ${
                        member.status === "online"
                          ? "online"
                          : member.status === "in-game"
                          ? "in-game"
                          : member.status === "labs"
                          ? "labs"
                          : "idle"
                      }`}
                    ></div>
                  )}
                </div>
                <div className="member-info flex-1 min-w-0">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                    <span className="member-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 1 }}>{member.name || member.username}</span>
                    <MemberBadge badge={member.badge || member.division} />
                  </div>
                  {member.activity && (
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                      {member.activityType === 'listening' ? '🎵' : member.activityType === 'watching' ? '📺' : member.activityType === 'competing' ? '🏆' : '🎮'} {member.activity}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}

        {/* Division-based groups (fallback for members without recognized roles) */}
        {Object.entries(divisionGrouped).map(([section, sectionMembers]) => (
          <div key={section} className="member-section">
            <div className="member-section-title">
              {section} — {sectionMembers.length}
            </div>
            {sectionMembers.map((member) => (
              <div
                key={member.id}
                className="member-item group relative"
                onClick={() => onOpen("userProfile", { member })}
              >
                <div className={`member-avatar-small flex-shrink-0 ${member.avatarBg || ''}`}>
                  {member.avatar_url ? (
                    <img src={member.avatar_url} alt={member.name} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    member.avatar
                  )}
                  {member.status !== "offline" && (
                    <div
                      className={`online-indicator ${
                        member.status === "online"
                          ? "online"
                          : member.status === "in-game"
                          ? "in-game"
                          : member.status === "labs"
                          ? "labs"
                          : "idle"
                      }`}
                    ></div>
                  )}
                </div>
                <div className="member-info flex-1 min-w-0">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                    <span className="member-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 1 }}>{member.name}</span>
                    <MemberBadge badge={member.badge || member.division} />
                  </div>
                  {member.activity && (
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                      {member.activityType === 'listening' ? '🎵' : member.activityType === 'watching' ? '📺' : member.activityType === 'competing' ? '🏆' : '🎮'} {member.activity}
                    </div>
                  )}
                </div>
                <button
                  className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 w-6 h-6 rounded flex items-center justify-center hover:bg-white/10 text-gray-400 hover:text-white flex-shrink-0"
                  title={`Message ${member.name}`}
                  onClick={(e) => { e.stopPropagation(); onOpen("directMessage", { member }); }}
                >
                  <span className="text-xs">💬</span>
                </button>
              </div>
            ))}
          </div>
        ))}
        </>)}
      </div>
    </div>
  );
}
